import { memo } from 'react';
import { ICalendarEvent } from '../../@types/calendar';
import { cn } from '../../lib/utils';

interface IEventCardProps {
  event: ICalendarEvent;
  eventCount?: number; // Number of events in this slot (for count badge)
}

/**
 * EventCard Component
 * Renders individual calendar events with status-based styling
 *
 * Status colors:
 * - scheduled: blue (primary)
 * - published: green (success)
 * - failed: red (destructive)
 * - publishing: yellow (warning)
 */
const EventCard = ({ event, eventCount }: IEventCardProps) => {
  // Status-based styling (no border classes - border is handled by calendar wrapper)
  const statusClasses: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-900',
    published: 'bg-green-100 text-green-900',
    failed: 'bg-red-100 text-red-900',
    publishing: 'bg-yellow-100 text-yellow-900',
    cancelled: 'bg-gray-100 text-gray-900',
  };

  return (
    <div
      className={cn(
        'flex flex-col px-1 py-0.5 text-sm h-full w-full',
        statusClasses[event.status] ?? 'bg-gray-100 text-gray-900',
        event.isDraggable && 'cursor-move',
        !event.isDraggable && 'cursor-pointer'
      )}
    >
      {/* Title - full width without truncate */}
      <div className="font-semibold break-words leading-tight">{event.title}</div>

      {/* Target accounts count */}
      {event.targetAccounts.length > 0 && (
        <div className="text-xs opacity-75 mt-0.5">
          {event.targetAccounts.length} account{event.targetAccounts.length > 1 ? 's' : ''}
        </div>
      )}

      {/* Count badge for multiple posts in same slot */}
      {eventCount && eventCount > 1 && (
        <div className="mt-1 inline-flex w-fit rounded-full bg-black bg-opacity-20 px-2 py-0.5 text-xs font-bold">
          +{eventCount - 1} more
        </div>
      )}

      {/* Error indicator */}
      {event.status === 'failed' && event.errorMessage && (
        <div className="mt-1 text-xs font-medium">⚠ Failed</div>
      )}
    </div>
  );
};

export default memo(EventCard);
